import React, { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import { IoSearchOutline } from 'react-icons/io5';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import Manager_web_portal from '../../../layout/Manager_web_portal';
import * as XLSX from 'xlsx';
import ViewSlip from '../CompanyExpense/ViewSlip';
import { ClosureTableSkeleton, Skeleton } from '../../../components/Skeleton';
import { Base_url } from '../../../utils/Base_url';

const Comming = () => {
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [getData, setGetData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [filteredDeposits, setFilteredDeposits] = useState([]);

  // Modal states
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [totalProfit, setTotalProfit] = useState('');
  const [totalDeduction, setTotalDeduction] = useState('');
  const [totalLoss, setTotalLoss] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isCnicModalOpen, setIsCnicModalOpen] = useState(false);
  const [cnicInput, setCnicInput] = useState('');
  const [isCnicSearchMode, setIsCnicSearchMode] = useState(false);
  const [cnicErrorMessage, setCnicErrorMessage] = useState('');
  const [isCnicSearching, setIsCnicSearching] = useState(false);
  const [pushingId, setPushingId] = useState(null);

  const currentPageData = filteredDeposits.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  // Fetch data from the new API
  useEffect(() => {
    fetchSoonEnrollment();
  }, []);

  const fetchSoonEnrollment = async () => {
    setIsFetching(true);
    setIsCnicSearchMode(false);
    try {
      const requestOptions = {
        method: "GET",
        redirect: "follow"
      };

      const response = await fetch(
        `${Base_url}/v1/worker/soon-enrollement`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();

      if (result.success && result.closingSoon) {
        // Use the API data directly with proper field names
        setData(result.closingSoon);
        setFilteredDeposits(result.closingSoon);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsFetching(false);
    }
  };

  // Fetch enrollments by CNIC
  const fetchEnrollmentsByCnic = async (cnic) => {
    if (!cnic.trim()) {
      setCnicErrorMessage('Please enter a valid CNIC');
      return;
    }

    setIsCnicSearching(true);
    setCnicErrorMessage('');

    try {
      const requestOptions = {
        method: "GET",
        redirect: "follow"
      };

      const response = await fetch(
        `${Base_url}/v1/worker/enrollement-by-cnic?cnic=${cnic}&excludeRunning=true`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();

      if (result.success && result.enrollments && result.enrollments.length > 0) {
        // Data found - close modal and set data
        setData(result.enrollments);
        setFilteredDeposits(result.enrollments);
        setIsCnicSearchMode(true);
        setIsCnicModalOpen(false);
        setCnicInput('');
        setCnicErrorMessage('');
      } else {
        // No data found - show message in modal
        setCnicErrorMessage('No upcoming enrollments found for this CNIC');
        setData([]);
        setFilteredDeposits([]);
      }
    } catch (error) {
      console.error('Error fetching data by CNIC:', error);
      setCnicErrorMessage('Failed to fetch enrollments. Please check the CNIC and try again.');
      setData([]);
      setFilteredDeposits([]);
    } finally {
      setIsCnicSearching(false);
    }
  };

  // Handle CNIC search submit
  const handleCnicSearch = () => {
    if (!cnicInput.trim()) {
      setCnicErrorMessage('Please enter a CNIC number');
      return;
    }
    setCnicErrorMessage('');
    fetchEnrollmentsByCnic(cnicInput.trim());
  };

  // Filter function
  useEffect(() => {
    filterDeposits();
  }, [data, searchTerm]);

  const filterDeposits = () => {
    let filtered = data || [];

    if (searchTerm.trim()) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const nameMatch = item?.investorName?.toLowerCase().includes(lowercasedSearchTerm);
        const cnicMatch = item?.investorCnic?.toLowerCase().includes(lowercasedSearchTerm);
        const planMatch = item?.planId?.planName?.toLowerCase().includes(lowercasedSearchTerm);
        const planIdMatch = item?.planId?._id?.toLowerCase().includes(lowercasedSearchTerm);

        return nameMatch || cnicMatch || planMatch || planIdMatch;
      });
    }

    setFilteredDeposits(filtered);
    setCurrentPage(1);
  };

  // Open close modal
  const openCloseModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setTotalProfit(enrollment?.totalProfit?.toString() || '');
    setTotalDeduction(enrollment?.totalDeduction?.toString() || '');
    setTotalLoss(enrollment?.totalLoss?.toString() || '');
    setErrorMessage('');
    setSuccessMessage('');
    setIsCloseModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsCloseModalOpen(false);
    setSelectedEnrollment(null);
    setTotalProfit('');
    setTotalDeduction('');
    setTotalLoss('');
    setErrorMessage('');
    setSuccessMessage('');
    setIsSubmitting(false);
  };

  // Validate financial inputs
  const validateFinancialInputs = () => {
    // Check if at least profit or loss is provided
    if (!totalProfit.trim() && !totalLoss.trim()) {
      setErrorMessage('Either Total Profit or Total Loss is required');
      return false;
    }

    // Validate profit if provided
    if (totalProfit.trim()) {
      const profitNum = parseFloat(totalProfit);
      if (isNaN(profitNum) || profitNum < 0) {
        setErrorMessage('Total Profit must be a valid positive number');
        return false;
      }
    }

    // Validate loss if provided
    if (totalLoss.trim()) {
      const lossNum = parseFloat(totalLoss);
      if (isNaN(lossNum) || lossNum < 0) {
        setErrorMessage('Total Loss must be a valid positive number');
        return false;
      }
    }

    // Validate deduction if provided (optional)
    if (totalDeduction.trim()) {
      const deductionNum = parseFloat(totalDeduction);
      if (isNaN(deductionNum) || deductionNum < 0) {
        setErrorMessage('Total Deduction must be a valid positive number');
        return false;
      }
    }

    return true;
  };

  // Handle close plan submission
  const handleClosePlan = async () => {
    // Validate inputs
    if (!validateFinancialInputs()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      // Prepare request body with all fields
      const requestBody = {
        "totalProfit": totalProfit.trim() ? parseFloat(totalProfit) : 0,
        "totalDeduction": totalDeduction.trim() ? parseFloat(totalDeduction) : 0,
        "totalLoss": totalLoss.trim() ? parseFloat(totalLoss) : 0
      };

      const raw = JSON.stringify(requestBody);

      const requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      const response = await fetch(
        `${Base_url}/v1/worker/close-enrollement/${selectedEnrollment._id}`,
        requestOptions
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessMessage('Enrollment closed successfully!');

        // Reload data after 1.5 seconds
        setTimeout(() => {
          closeModal();
          // Reload data based on current mode
          if (isCnicSearchMode && selectedEnrollment?.investorCnic) {
            fetchEnrollmentsByCnic(selectedEnrollment.investorCnic);
          } else {
            fetchSoonEnrollment();
          }
        }, 1500);
      } else {
        setErrorMessage(result.message || 'Failed to close enrollment. Please try again.');
      }
    } catch (error) {
      console.error('Error closing enrollment:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePushToRunning = async (enrollment) => {
    if (!enrollment?._id || pushingId) return;

    setPushingId(enrollment._id);
    try {
      const response = await fetch(
        `${Base_url}/v1/worker/push-to-running/${enrollment._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      const result = await response.json();

      if (response.ok && result.success) {
        setData((prev) => prev.filter((item) => item._id !== enrollment._id));
        setFilteredDeposits((prev) => prev.filter((item) => item._id !== enrollment._id));
      } else {
        alert(result.message || 'Failed to push to Running Cases');
      }
    } catch (error) {
      console.error('Error pushing to running cases:', error);
      alert('Network error. Please try again.');
    } finally {
      setPushingId(null);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK');
  };

  // Get plan name safely
  const getPlanName = (item) => {
    if (item?.planId?.planName) {
      return item.planId.planName;
    }
    // Fallback if planId is a string (old structure)
    return item?.planId || 'N/A';
  };

  // Get plan ID safely
  const getPlanId = (item) => {
    if (item?.planId?._id) {
      return item.planId._id.slice(-8); // Show last 8 characters
    }
    // Fallback if planId is a string (old structure)
    return item?.planId?.slice(-8) || 'N/A';
  };

  return (
    <>
      <Manager_web_portal
        language={<p className="sm:block hidden">English | Urdu</p>}
        ToggleBtn={
          <div className="flex gap-4 items-center">
            <HiOutlineArrowLeft />
            {/* <h2 className="">Soon Enrollment Investors</h2> */}
          </div>
        }
        title_props={
          <div className="w-96 relative">
            <Input
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={'Search'}
              className={'border bg-white w-full rounded-full'}
            />
            <Button
              Icon={<IoSearchOutline className="text-white text-xl" />}
              label={'search'}
              className={'absolute top-0 right-0 bg-secondary text-white uppercase font-normal rounded-full'}
            />
          </div>
        }
      >
        <div className="w-full mb-4 sm:hidden block relative">
          <Input
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={'Search by name, CNIC or Plan Name'}
            className={'border bg-white w-full rounded-full'}
          />
          <Button
            Icon={<IoSearchOutline className="text-white text-xl" />}
            label={'search'}
            className={'absolute top-0 right-0 bg-secondary text-white uppercase font-normal rounded-full'}
          />
        </div>

        <div className="sm:flex block justify-between items-center mb-6">
          <div>
            {/* <h2 className="sm:block hidden">Soon Enrollment Investors</h2> */}
          </div>
          <div className="flex gap-3 items-center">
            {isCnicSearchMode && (
              <button
                onClick={fetchSoonEnrollment}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm transition-colors shadow-sm flex items-center gap-2"
              >
                <HiOutlineArrowLeft />
                Back to Default View
              </button>
            )}
            <button
              onClick={() => {
                setIsCnicModalOpen(true);
                setCnicErrorMessage('');
                setCnicInput('');
              }}
              className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md text-sm transition-colors shadow-sm"
            >
              Search by CNIC
            </button>
          </div>
        </div>

        <section className="mb-20 text-gray-800">
          <div className="block rounded-lg bg-white shadow">
            <div className="overflow-hidden">
              <div className="w-full">
                <div className="min-w-full">
                  <div className="overflow-x-auto">
                    <table className="min-w-full mb-0">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4 text-left">
                            Investor Name
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4 text-center">
                            CNIC No
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4 text-center">
                            Plan Name
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4 text-center">
                            Years
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4 text-center">
                            Total Deposit
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4 text-center">
                            Deposit Date
                          </th>
                          <th scope="col" className="text-sm text-darkGray font-medium px-6 py-4 text-center whitespace-nowrap min-w-[180px]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {isFetching || isCnicSearching ? (
                          <ClosureTableSkeleton rows={itemsPerPage} />
                        ) : currentPageData?.length > 0 ? (
                          currentPageData.map((item, index) => (
                            <tr key={item._id} className="border-b hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Link
                                  onClick={() => localStorage.setItem('iName', item?.investorName)}
                                  to={`/manager/comming/details/${item?.investorCnic}/${item?._id}`}
                                  className="font-medium text-secondary hover:text-primary transition-colors"
                                >
                                  {item?.investorName || 'N/A'}
                                </Link>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="inline-block text-sm text-black py-1 px-3 bg-blue-50 rounded-full">
                                  {item?.investorCnic || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="inline-block text-sm text-black py-1 px-3 bg-purple-50 rounded-full">
                                  {getPlanName(item)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="inline-block text-sm text-black py-1 px-3 bg-yellow-50 rounded-full">
                                  {item?.numberOfYears || 0} {item?.numberOfYears === 1 ? 'year' : 'years'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="inline-block text-sm text-black py-1 px-3 bg-green-50 rounded-full font-medium">
                                  Rs. {formatCurrency(item?.totalDepositedAmount || 0)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="inline-block text-sm text-black py-1 px-3 bg-indigo-50 rounded-full">
                                  {formatDate(item?.firstDepositDate) || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex justify-center items-center gap-2">
                                  {item?.status === 'active' ? (
                                    <>
                                      <button
                                        className="bg-secondary hover:bg-primary text-white min-w-[72px] px-4 py-2 rounded-md text-sm transition-colors shadow-sm"
                                        onClick={() => openCloseModal(item)}
                                      >
                                        Close
                                      </button>
                                      <button
                                        className="bg-primary hover:bg-secondary text-white min-w-[72px] px-4 py-2 rounded-md text-sm transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                        onClick={() => handlePushToRunning(item)}
                                        disabled={pushingId === item._id}
                                        title="Move to Running Cases"
                                      >
                                        {pushingId === item._id ? '...' : 'Move'}
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-gray-400 text-sm px-4 py-2">
                                      Closed
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-8">
                              <div className="flex flex-col items-center justify-center">
                                <p className="text-gray-500 text-lg mb-2">No soon enrollment data found</p>
                                <p className="text-gray-400 text-sm">
                                  {searchTerm ? 'Try a different search term' : 'All enrollments are up to date'}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {filteredDeposits.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center py-4 px-6 border-t bg-gray-50">
                <div className="text-sm text-gray-600 mb-3 sm:mb-0">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredDeposits.length)} to {Math.min(currentPage * itemsPerPage, filteredDeposits.length)} of {filteredDeposits.length} entries
                </div>
                <div className="flex items-center gap-4">
                  <button
                    className={`${currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-secondary text-white hover:bg-primary'
                      } flex justify-center items-center w-9 h-9 rounded-md transition-colors shadow-sm`}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <FaAngleLeft size={18} />
                  </button>
                  <span className="text-primary font-medium text-sm">
                    Page {currentPage} of {Math.ceil(filteredDeposits.length / itemsPerPage)}
                  </span>
                  <button
                    className={`${currentPage * itemsPerPage >= filteredDeposits.length
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-secondary text-white hover:bg-primary'
                      } flex justify-center items-center w-9 h-9 rounded-md transition-colors shadow-sm`}
                    disabled={currentPage * itemsPerPage >= filteredDeposits.length}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <FaAngleRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        <ViewSlip isModalOpen={isModalOpen2} setIsModalOpen={setIsModalOpen2} images={getData} />
      </Manager_web_portal>

      {/* Close Plan Modal */}
      {isCloseModalOpen && selectedEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Close Enrollment</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isSubmitting}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Investor Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">Investor Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <p className="font-medium">{selectedEnrollment.investorName}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">CNIC:</span>
                    <p className="font-medium">{selectedEnrollment.investorCnic}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Plan Name:</span>
                    <p className="font-medium">{getPlanName(selectedEnrollment)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Deposit:</span>
                    <p className="font-medium">Rs. {formatCurrency(selectedEnrollment.totalDeposit || 0)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Years:</span>
                    <p className="font-medium">{selectedEnrollment.numberOfYears} years</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Payment:</span>
                    <p className="font-medium capitalize">{selectedEnrollment.paymentFrequency || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Form Inputs */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Profit <span className="text-gray-400 text-xs">(optional if loss entered)</span>
                  </label>
                  <Input
                    type="number"
                    value={totalProfit}
                    onChange={(e) => setTotalProfit(e.target.value)}
                    placeholder="Enter total profit"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Loss <span className="text-gray-400 text-xs">(optional if profit entered)</span>
                  </label>
                  <Input
                    type="number"
                    value={totalLoss}
                    onChange={(e) => setTotalLoss(e.target.value)}
                    placeholder="Enter total loss"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Deduction <span className="text-gray-400 text-xs">(optional)</span>
                  </label>
                  <Input
                    type="number"
                    value={totalDeduction}
                    onChange={(e) => setTotalDeduction(e.target.value)}
                    placeholder="Enter total deduction"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Messages */}
                {errorMessage && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {errorMessage}
                    </p>
                  </div>
                )}

                {successMessage && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-600 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {successMessage}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClosePlan}
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-secondary hover:bg-primary'
                    }`}
                >
                  {isSubmitting ? (
                    <Skeleton className="skeleton-bone-soft h-4 w-24 rounded-md bg-transparent" />
                  ) : (
                    'Close Enrollment'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CNIC Search Modal */}
      {isCnicModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Search by CNIC</h3>
                <button
                  onClick={() => {
                    if (!isCnicSearching) {
                      setIsCnicModalOpen(false);
                      setCnicInput('');
                      setCnicErrorMessage('');
                    }
                  }}
                  disabled={isCnicSearching}
                  className={`text-gray-400 hover:text-gray-600 transition-colors ${isCnicSearching ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter CNIC Number
                  </label>
                  <Input
                    type="text"
                    value={cnicInput}
                    onChange={(e) => {
                      setCnicInput(e.target.value);
                      setCnicErrorMessage('');
                    }}
                    placeholder="Enter CNIC (e.g., 3840349195649)"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isCnicSearching}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isCnicSearching) {
                        handleCnicSearch();
                      }
                    }}
                  />
                </div>

                {cnicErrorMessage && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {cnicErrorMessage}
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                  <button
                    onClick={() => {
                      setIsCnicModalOpen(false);
                      setCnicInput('');
                      setCnicErrorMessage('');
                    }}
                    disabled={isCnicSearching}
                    className={`px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors ${isCnicSearching ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCnicSearch}
                    disabled={isCnicSearching}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isCnicSearching
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary hover:bg-primary-dark'
                      }`}
                  >
                    {isCnicSearching ? (
                      <Skeleton className="skeleton-bone-soft h-4 w-16 rounded-md bg-transparent" />
                    ) : (
                      'Search'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Comming;